import { useCallback, useEffect, useRef, useState } from "react";
import {
  parsePlansList,
  type Plan,
  type PlanAssigneesPatch,
} from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";
import { usePendingPlanCopies } from "../Programs/usePendingProgramCopies";
import { useProgramsInvalidator } from "../Programs/programsInvalidator";

// Mirrors apps/mobile's useAsyncResource status union without coupling to
// the mobile hook — hosts map their resource status onto this. (Lived in
// PlansView until HR-S4 retired that surface for the hub's Library.)
export type PlansResourceStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

// Shared plans-list data layer for the web-rnw console. Mobile's PlanScreen
// keeps its own useAsyncResource (AsyncStorage cache + focus/AppState
// re-kick) for now; both go through the shared parsePlansList, so the
// parse/sort is single-source. Convergence onto this hook is tracked in
// next/anti-monster.md. apiFetch via a ref so a fresh literal per render
// never re-kicks the fetch (the unstable-object-dep loop — see
// useAsyncResource / usePrograms).
export type UsePlansOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Bump to re-kick the fetch (e.g. after a mutation elsewhere). */
  refreshSignal?: number;
  /** false: no fetch yet. Each turn to true fetches again (#368: the diary's
   *  calendar, read when it is shown). Default true. */
  enabled?: boolean;
};

export function usePlans(opts: UsePlansOptions) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<PlansResourceStatus>("idle");

  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
  });

  const refresh = useCallback(async (): Promise<void> => {
    setStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    try {
      const raw = await apiRef.current<unknown>("/api/me/plans");
      setPlans(parsePlansList(raw));
      setStatus("loaded");
    } catch {
      setStatus("error");
    }
  }, []);

  const enabled = opts.enabled ?? true;
  useEffect(() => {
    if (enabled) void refresh();
  }, [refresh, opts.refreshSignal, enabled]);

  // #475: the host tells the plans lists when a queued plan create lands.
  const plansChannel = useProgramsInvalidator()?.plans;
  useEffect(() => {
    if (!plansChannel || !enabled) return;
    return plansChannel.subscribe(() => void refresh());
  }, [plansChannel, enabled, refresh]);

  // Mirrors usePrograms.deleteProgram: re-fetch even on outcome (the server
  // may have partially moved) and hand the error back instead of rendering.
  // A queued delete returns null — for the athlete it succeeded, and on
  // mobile it is durable (DX-2/F).
  const deletePlan = useCallback(
    async (id: string): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "DELETE",
        path: `/api/me/plans/${encodeURIComponent(id)}`,
      });
      void refresh();
      return writeError(result);
    },
    [refresh],
  );

  // CO-21 — the assignees-only partial, twin of usePrograms' setProgramAssignees
  // (packages/shared's PlanAssigneesPatch): the standalone Assign sheet on a
  // Library card saves just `assignedTo`/`assigneeEmails`, never the whole
  // plan. Mirrors deletePlan's shape: re-fetch regardless of outcome, hand
  // the error back instead of rendering it.
  const setPlanAssignees = useCallback(
    async (id: string, body: PlanAssigneesPatch): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "PATCH",
        path: `/api/me/plans/${encodeURIComponent(id)}/assignees`,
        body,
      });
      void refresh();
      return writeError(result);
    },
    [refresh],
  );

  // #475: plan copies a queued write promised and the list does not show
  // yet. Reconciled only once the list is read: `plans` is [] before that.
  const read = status === "loaded" || status === "refreshing";
  const { pendingKeys, holdCopy } = usePendingPlanCopies(read ? plans : null);

  return {
    plans,
    status,
    refresh,
    deletePlan,
    setPlanAssignees,
    pendingCopyKeys: pendingKeys,
    holdCopy,
  };
}
